import React, { FC, useEffect, useState } from "react";
import {
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  makeStyles,
  Typography,
} from "@material-ui/core";
import {
  AutocompleteInput,
  BooleanInput,
  Button,
  ReferenceInput,
  required,
} from "react-admin";
import Alert from "@material-ui/lab/Alert";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import SubcontractorTimesheetAdhocFeeInput from "./SubcontractorTimesheetAdhocFeeInput";
import { useField, useFormState } from "react-final-form";

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
  cancelButton: {
    backgroundColor: "#eeeeee",
    color: "black",
  },
  alertToggleChangeSubcontractor: {
    marginTop: "-20px",
    marginBottom: "25px",
  },
  alertToggleChangeSubcontractorParagraph: {
    marginBottom: "5px",
  },
});

interface NewSubcontractorInputProps {
  adhocSubcontractorId: number;
}

const NewSubcontractorInput: FC<NewSubcontractorInputProps> = ({
  adhocSubcontractorId,
}) => {
  const classes = useStyles();
  // Only subscribe to values to avoid unnecessary re-renders
  const { values } = useFormState({ subscription: { values: true } });

  const {
    subcontractorId = "",
    newSubcontractorId = "",
    lastSavedTotalStudent = 0,
    lastSavedVehicleSize = "",
    hasExistingAdhocFees = false,
  } = values;

  const isUpdateSubcontractorField = useField("isUpdateSubcontractor");
  const totalStudentField = useField("totalStudent");
  const vehicleSizeField = useField("vehicleSize");
  const newSubcontractorField = useField("newSubcontractorId");

  const [open, setOpen] = useState(false);
  const [isUpdateSubcontractor, setIsUpdateSubcontractor] = useState(false);
  const [newToggleValue, setNewToggleValue] = useState(false);

  const handleToggleChange = (value: boolean) => {
    setNewToggleValue(value);
    // If toggling ON (from false to true), show confirmation dialog
    if (!isUpdateSubcontractor && value) {
      setOpen(true);
    } else {
      // If toggling OFF (from true to false), update directly without dialog
      setIsUpdateSubcontractor(value);
      isUpdateSubcontractorField.input.onChange(value);
    }
  };

  const handleConfirm = () => {
    setOpen(false); // close confirmation dialog
    setIsUpdateSubcontractor(newToggleValue);
    isUpdateSubcontractorField.input.onChange(newToggleValue);

    // Revert the total student value and vehicle size to the last saved value in database
    totalStudentField.input.onChange(lastSavedTotalStudent);
    vehicleSizeField.input.onChange(lastSavedVehicleSize);
  };

  const handleCancel = () => {
    setOpen(false); // close confirmation dialog

    // Revert the toggle value to the last confirmed one
    isUpdateSubcontractorField.input.onChange(isUpdateSubcontractor);

    setNewToggleValue(isUpdateSubcontractor);
  };

  useEffect(() => {
    if (adhocSubcontractorId) {
      setIsUpdateSubcontractor(true);
      newSubcontractorField.input.onChange(adhocSubcontractorId);

      // Revert the total student value and vehicle size to the last saved value in database
      totalStudentField.input.onChange(lastSavedTotalStudent);
      vehicleSizeField.input.onChange(lastSavedVehicleSize);
    }
  }, [adhocSubcontractorId]);

  return (
    <>
      <Grid item xs={4}>
        <BooleanInput
          source="isUpdateSubcontractor"
          label="Change Subcontractor"
          onChange={handleToggleChange}
          disabled={hasExistingAdhocFees}
        />
        {hasExistingAdhocFees && (
          <Alert
            severity="warning"
            className={classes.alertToggleChangeSubcontractor}
          >
            <Typography
              variant="body2"
              className={classes.alertToggleChangeSubcontractorParagraph}
            >
              You cannot change the Subcontractor again because Subcontractor
              Adhoc Fees have already been created for both subcontractors.
            </Typography>
            <Typography
              variant="body2"
              className={classes.alertToggleChangeSubcontractorParagraph}
            >
              If the Subcontractor Adhoc Fee{" "}
              <strong>
                is not issued yet (still in Pending, Approved, or Rejected)
              </strong>
              , Please ask <strong>Sector Lead</strong> to delete the related
              Subcontractor Adhoc Fee in the Subcontractor Finance menu before
              trying again.
            </Typography>
            <Typography
              variant="body2"
              className={classes.alertToggleChangeSubcontractorParagraph}
            >
              Use the <strong>"Go to Subcontractor Finance"</strong> button for
              each subcontractor to review or manage their Adhoc Fees directly.
            </Typography>
          </Alert>
        )}
      </Grid>
      <Grid item xs={8}></Grid>

      {isUpdateSubcontractor && (
        <>
          <Grid item xs={4} style={{ marginTop: "-35px" }}>
            <ReferenceInput
              label="New Subcontractor"
              source="newSubcontractorId"
              reference="subcontractors"
              validate={required()}
              filter={{ Status: "active" }}
              perPage={25}
              filterToQuery={(searchText: any) => {
                if (searchText !== "") return { search: searchText };
              }}
              disabled={!!adhocSubcontractorId}
            >
              <AutocompleteInput
                optionText={(choice: any) =>
                  `${choice.name} (${choice.subcontractorIdentifier})`
                }
              />
            </ReferenceInput>
          </Grid>
          <Grid item xs={8}></Grid>

          <Grid item xs={8}>
            <Alert
              severity="info"
              className={classes.alertToggleChangeSubcontractor}
            >
              Updating Subcontractor will generate new Subcontractor Adhoc Fee
              for both old and new Subcontractor.
              <br />
              If no old Subcontractor is selected, only the new Subcontractor's
              Adhoc Fee will be created.
              <br />
              <br />
              By default, the amount entered is considered a positive value.
              <br />
              If you want to create a deduction (negative value), please prefix
              the amount with a minus sign (e.g., <strong>-100</strong>).
            </Alert>
          </Grid>
          <Grid item xs={4}></Grid>
        </>
      )}

      {/* Adhoc Fee sections for old and new */}
      {isUpdateSubcontractor && newSubcontractorId && (
        <>
          <Grid item xs={8}>
            <Grid container spacing={4}>
              {subcontractorId && (
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardHeader title="Old Subcontractor Adhoc Fee" />
                    <CardContent>
                      <SubcontractorTimesheetAdhocFeeInput fieldPrefix="old" />
                    </CardContent>
                  </Card>
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardHeader title="New Subcontractor Adhoc Fee" />
                  <CardContent>
                    <SubcontractorTimesheetAdhocFeeInput fieldPrefix="new" />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={4}></Grid>
        </>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="md"
      >
        <DialogTitle id="alert-dialog-title">Change Subcontractor</DialogTitle>
        <DialogContent>
          <DialogContentText color="initial">
            Changing the Subcontractor will reset the{" "}
            <strong>Total Student</strong> and <strong>Vehicle Size</strong>{" "}
            fields to their previously saved values.
            <br />
            Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            label="Cancel"
            color="secondary"
            onClick={handleCancel}
            className={classes.cancelButton}
          />
          <Button label="Confirm" color="primary" onClick={handleConfirm} />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NewSubcontractorInput;
